import React from "react"
import {
  type FloatingRootContext,
  type OpenChangeReason,
  type UseInteractionsReturn,
  useClick,
  useDismiss,
  useFloatingRootContext,
  useInteractions,
  useListNavigation,
} from "@floating-ui/react"

const context = React.createContext<
  | (UseInteractionsReturn & {
      anchorRef: React.Ref<Element>
      actionListRef: React.Ref<HTMLElement>
      rootContext: FloatingRootContext
      listRef: React.RefObject<(HTMLElement | null)[]>
    })
  | null
>(null)

const ActionListRoot = React.memo(function ActionListRoot(props: {
  open?: boolean
  onOpenChange?: (
    open: boolean,
    event?: Event,
    reason?: OpenChangeReason
  ) => void
  children?: React.ReactNode | ((open: boolean) => React.ReactNode)
}) {
  const [open, setOpen] = React.useState(false)

  const [anchor, anchorRef] = React.useState<Element | null>(null)
  const [actionList, actionListRef] = React.useState<HTMLElement | null>(null)

  const rootContext = useFloatingRootContext({
    open: props.open ?? open,
    onOpenChange: props.onOpenChange ?? setOpen,
    elements: {
      reference: anchor,
      floating: actionList,
    },
  })

  const click = useClick(rootContext)
  const dismiss = useDismiss(rootContext)

  const [activeIndex, setActiveIndex] = React.useState<number | null>(null)
  const listRef = React.useRef<(HTMLElement | null)[]>([])
  const listNav = useListNavigation(rootContext, {
    listRef,
    activeIndex,
    onNavigate: setActiveIndex,
    loop: true,
  })

  const { getReferenceProps, getFloatingProps, getItemProps } = useInteractions(
    [listNav, click, dismiss]
  )

  const value = React.useMemo(
    () => ({
      rootContext,
      anchorRef,
      actionListRef,
      listRef,
      getReferenceProps,
      getFloatingProps,
      getItemProps,
    }),
    [
      rootContext,
      getReferenceProps,
      getFloatingProps,
      getItemProps,
      anchorRef,
      actionListRef,
      listRef,
    ]
  )

  return (
    <context.Provider value={value}>
      {typeof props.children === "function"
        ? props.children(open)
        : props.children}
    </context.Provider>
  )
})

export default ActionListRoot

export const useActionListRoot = () => {
  const contextValue = React.useContext(context)
  if (!contextValue) {
    throw new Error("Missing ActionListRoot")
  }
  return contextValue
}
